// Permission Management API Service
import { getData, Postdata, patchData } from '../../../API/GlobalApi.js';

const BASE_ENDPOINT = 'core';

// Simple cache for frequently accessed data
const cache = {
  moduleTree: null,
  moduleTreeTimestamp: null,
  stats: null,
  statsTimestamp: null,
  CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

export class PermissionService {
  // Get nested module/submodule/permission structure with caching
  static async getModuleTree() {
    try {
      const now = Date.now();
      // Use cache if available and not expired
      if (cache.moduleTree && cache.moduleTreeTimestamp &&
          (now - cache.moduleTreeTimestamp) < cache.CACHE_DURATION) {
        return cache.moduleTree;
      }

      const response = await getData(`${BASE_ENDPOINT}/modules/tree/`);

      // Cache the response
      cache.moduleTree = response;
      cache.moduleTreeTimestamp = now;

      return response;
    } catch (error) {
      console.error('Error fetching module tree:', error);
      throw error;
    }
  }

  // Get all users with their permissions
  static async getUsersWithPermissions() {
    try {
      const response = await getData(`${BASE_ENDPOINT}/user-permissions/`);
      return response;
    } catch (error) {
      console.error('Error fetching users with permissions:', error);
      throw error;
    }
  }

  // Get users grouped by profile with permissions
  static async getUsersGroupedByProfile(applyStudentFilter = true) {
    try {
      const params = new URLSearchParams();
      if (applyStudentFilter !== undefined) params.append('apply_student_filter', applyStudentFilter);

      const response = await getData(`${BASE_ENDPOINT}/user-permissions/grouped_by_profile/?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching grouped users:', error);
      throw error;
    }
  }

  // Create new permissions and clear relevant caches
  static async createPermission(data) {
    try {
      const response = await Postdata(`${BASE_ENDPOINT}/new-permissions/`, data);
      // Clear caches since we created new permissions
      this.clearModuleTreeCache();
      this.clearStatsCache();
      return response;
    } catch (error) {
      console.error('Error creating permission:', error);
      throw error;
    }
  }

  // Bulk assign/revoke permissions to/from users and clear stats cache
  static async bulkAssignPermissions(data) {
    try {
      const response = await Postdata(`${BASE_ENDPOINT}/permissions/bulk/`, data);
      // Clear stats cache since user permissions changed
      this.clearStatsCache();
      return response;
    } catch (error) {
      console.error('Error bulk assigning permissions:', error);
      throw error;
    }
  }

  // Get system statistics with caching
  static async getPermissionStats() {
    try {
      const now = Date.now();
      // Use cache if available and not expired
      if (cache.stats && cache.statsTimestamp &&
          (now - cache.statsTimestamp) < cache.CACHE_DURATION) {
        return cache.stats;
      }

      const response = await getData(`${BASE_ENDPOINT}/permissions/stats/`);

      // Cache the response
      cache.stats = response;
      cache.statsTimestamp = now;

      return response;
      return response;
    } catch (error) {
      console.error('Error fetching permission stats:', error);
      throw error;
    }
  }

  // Get all modules
  static async getModules() {
    try {
      const response = await getData(`${BASE_ENDPOINT}/modules/`);
      return response;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  }

  // Get submodules by module ID
  static async getSubmodules(moduleId) {
    try {
      const response = await getData(`${BASE_ENDPOINT}/modules/${moduleId}/submodules/`);
      return response;
    } catch (error) {
      console.error('Error fetching submodules:', error);
      throw error;
    }
  }

  // Get permissions by submodule ID
  static async getPermissions(submoduleId) {
    try {
      const response = await getData(`${BASE_ENDPOINT}/submodules/${submoduleId}/permissions/`);
      return response;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  }

  // Update permission
  static async updatePermission(id, data) {
    try {
      const response = await patchData(`${BASE_ENDPOINT}/permissions/${id}/`, data);
      return response;
    } catch (error) {
      console.error('Error updating permission:', error);
      throw error;
    }
  }

  // Get single user permissions
  static async getUserPermissions(userId) {
    try {
      const response = await getData(`${BASE_ENDPOINT}/user-permissions/${userId}/`);
      return response;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      throw error;
    }
  }

  // Update user permissions
  static async updateUserPermissions(userId, permissionIds) {
    try {
      const response = await patchData(`${BASE_ENDPOINT}/user-permissions/${userId}/`, {
        permission_ids: permissionIds
      });
      return response;
    } catch (error) {
      console.error('Error updating user permissions:', error);
      throw error;
    }
  }

  // Get independent permissions (not associated with any module/submodule)
  static async getIndependentPermissions(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== undefined) params.append('status', filters.status);

      const response = await getData(`${BASE_ENDPOINT}/new-permissions/independent/?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching independent permissions:', error);
      throw error;
    }
  }

  // Cache management methods
  static clearCache() {
    cache.moduleTree = null;
    cache.moduleTreeTimestamp = null;
    cache.stats = null;
    cache.statsTimestamp = null;
  }

  static clearModuleTreeCache() {
    cache.moduleTree = null;
    cache.moduleTreeTimestamp = null;
  }

  static clearStatsCache() {
    cache.stats = null;
    cache.statsTimestamp = null;
  }
}
